/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ManufacturingMonitoring.module.scss';

type EventKind = 'Ownership' | 'Filing' | 'Officers' | 'Status' | 'Financial';

type EventTemplate = {
  hi: boolean;
  kind: EventKind;
  headline: string;
  entity: string;
  flag: string;
  source: string;
  filingType: string;
  identifier: string;
};

type Row = EventTemplate & {
  id: number;
  born: number;
  fresh: boolean;
};

const EVENTS: EventTemplate[] = [
  {
    hi: true,
    kind: 'Ownership',
    headline: 'Shareholding changed to 51%',
    entity: 'Vinh Loi Components JSC',
    flag: 'vn',
    source: 'NBRS — Vietnam',
    filingType: 'Share register update',
    identifier: 'SAMPLE-VN-04471',
  },
  {
    hi: false,
    kind: 'Financial',
    headline: 'Charge registered over trading assets',
    entity: 'Bharat Forgeworks Pvt Ltd',
    flag: 'in',
    source: 'MCA21 — India',
    filingType: 'Charge filing',
    identifier: 'SAMPLE-IN-88213',
  },
  {
    hi: false,
    kind: 'Filing',
    headline: 'Registered address changed',
    entity: 'Tekhan Kalıp Sanayi A.Ş.',
    flag: 'tr',
    source: 'MERSİS — Turkey',
    filingType: 'Register amendment',
    identifier: 'SAMPLE-TR-33017',
  },
  {
    hi: false,
    kind: 'Financial',
    headline: 'Annual accounts filed',
    entity: 'PolTherm Sp. z o.o.',
    flag: 'pl',
    source: 'KRS — Poland',
    filingType: 'Statutory accounts',
    identifier: 'SAMPLE-PL-71042',
  },
  {
    hi: true,
    kind: 'Status',
    headline: 'Struck off the register',
    entity: 'Zlínské Fügetechnik s.r.o.',
    flag: 'cz',
    source: 'Obchodní rejstřík — Czechia',
    filingType: 'Court register entry',
    identifier: 'SAMPLE-CZ-19985',
  },
  {
    hi: true,
    kind: 'Officers',
    headline: 'Managing director removed',
    entity: 'Nordwerk Präzisionsteile GmbH',
    flag: 'de',
    source: 'Handelsregister — Germany',
    filingType: 'Officer change',
    identifier: 'SAMPLE-DE-50218',
  },
  {
    hi: false,
    kind: 'Ownership',
    headline: 'Ultimate parent changed after acquisition',
    entity: 'Meridian Components Vietnam Co., Ltd',
    flag: 'vn',
    source: 'NBRS — Vietnam',
    filingType: 'Group filing',
    identifier: 'SAMPLE-0106482391',
  },
  {
    hi: true,
    kind: 'Status',
    headline: 'Insolvency proceedings opened',
    entity: 'Ceres Metalworks Kft.',
    flag: 'hu',
    source: 'Céginformáció — Hungary',
    filingType: 'Court notice',
    identifier: 'SAMPLE-HU-40276',
  },
  {
    hi: false,
    kind: 'Filing',
    headline: 'Business scope amended',
    entity: 'Officina Meccanica Torinese S.r.l.',
    flag: 'it',
    source: 'Registro Imprese — Italy',
    filingType: 'Register amendment',
    identifier: 'SAMPLE-IT-61190',
  },
  {
    hi: false,
    kind: 'Financial',
    headline: 'Charter capital increased',
    entity: 'Carpatica Componente S.A.',
    flag: 'ro',
    source: 'ONRC — Romania',
    filingType: 'Capital change filing',
    identifier: 'SAMPLE-RO-28864',
  },
];

const SEED_COUNT = 6;
const MAX_ROWS = 8;
const PUSH_MS = 2600;
const INITIAL_TODAY = 842;

const ago = (ms: number) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) {
    return `${s}s`;
  }
  if (s < 3600) {
    return `${Math.floor(s / 60)}m`;
  }
  return `${Math.floor(s / 3600)}h`;
};

const seedRows = (): Row[] =>
  Array.from({ length: SEED_COUNT }, (_, j) => ({
    ...EVENTS[j % EVENTS.length],
    id: j,
    born: Date.now() - j * 31000,
    fresh: false,
  }));

export const ManufacturingMonitoring = () => {
  const { t } = useInt();
  const [rows, setRows] = useState<Row[]>(seedRows);
  const [paused, setPaused] = useState(false);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [now, setNow] = useState<number | null>(null);
  const [today, setToday] = useState(INITIAL_TODAY);

  const liveRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(liveRef, { threshold: 0.2 });
  const pausedRef = useRef(paused);
  const idxRef = useRef(SEED_COUNT);
  const nextIdRef = useRef(SEED_COUNT);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const push = () => {
    if (pausedRef.current) {
      return;
    }

    const template = EVENTS[idxRef.current % EVENTS.length];
    idxRef.current += 1;
    const id = nextIdRef.current;
    nextIdRef.current += 1;

    setRows((prev) => {
      const next = [{ ...template, id, born: Date.now(), fresh: true }, ...prev];
      return next.length > MAX_ROWS ? next.slice(0, MAX_ROWS) : next;
    });
    setToday((t) => t + 1);

    setTimeout(() => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, fresh: false } : r)));
    }, 3400);
  };

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    setNow(Date.now());
    const clockId = setInterval(() => setNow(Date.now()), 1000);
    const pushId = setInterval(push, PUSH_MS);

    return () => {
      clearInterval(clockId);
      clearInterval(pushId);
    };
  }, [revealed]);

  const toggleOpen = (id: number) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Section background="#F3F8FF" className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            title={t('Registries publish every day. So do we.')}
            description={t(
              'Onboarding checks age the moment they are signed off. Put the whole base under a standing watch and read the changes instead of re-running the questionnaire.',
            )}
            align="left"
          />
        </div>

        <div className={styles.live} ref={liveRef}>
          <div className={styles.liveHead}>
            <span className={cn(styles.dot, paused && styles.dotPaused)} />
            <span className={cn(styles.liveTag, paused && styles.liveTagPaused)}>{t('Live change stream')}</span>
            <span className={styles.liveCount}>
              <b>{today.toLocaleString()}</b> {t('today')}
            </span>
            <button
              type="button"
              className={styles.pauseBtn}
              aria-pressed={paused}
              onClick={() => setPaused((p) => !p)}
            >
              <i />
              {paused ? t('Resume') : t('Pause')}
            </button>
          </div>

          <div className={styles.body}>
            {rows.map((row) => {
              const open = openIds.has(row.id);
              return (
                <div
                  className={cn(
                    styles.row,
                    row.hi && styles.rowHi,
                    row.fresh && styles.rowFresh,
                    open && styles.rowOpen,
                  )}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleOpen(row.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleOpen(row.id);
                    }
                  }}
                  key={row.id}
                >
                  <div className={styles.rowMain}>
                    <span className={styles.sev} />
                    <span className={styles.ent}>
                      <Flag code={row.flag} className={styles.entFlag} />
                      {row.entity}
                      {row.fresh && <i className={styles.newtag}>{t('New')}</i>}
                    </span>
                    <span className={styles.hl}>{row.headline}</span>
                    <span className={styles.src}>{row.source}</span>
                  </div>

                  {open && (
                    <div className={styles.det}>
                      <div className={styles.detIn}>
                        <div>
                          <span>{t('Filing')}</span>
                          <b>{row.filingType}</b>
                        </div>
                        <div>
                          <span>{t('Registry identifier')}</span>
                          <b>{row.identifier}</b>
                        </div>
                        <div>
                          <span>{t('Detected')}</span>
                          <b>
                            {now ? ago(now - row.born) : t('now')} {t('ago')}
                          </b>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.foot}>
            <span>{t('Sample stream')}</span>
            <span className={styles.hint}>{t('Select a row to see the filing behind it')}</span>
          </div>
        </div>
      </div>
    </Section>
  );
};
